import { useState } from 'react';

import Button from 'honeydew/Button';
import { LoadingIcon } from 'honeydew/Loader';
import { EBTFormSubmissionError } from 'shared/ebt/EBTErrors';
import ForageCardElement from 'shared/ebt/ForageCardElement/ForageCardElement';
import logError from 'utils/errorUtils';
import twclsx from 'utils/twclsx';

/**
 * Reusable EBT Card Form component for adding/editing EBT cards
 * Can be used in both account settings and payment flow contexts
 *
 * @param {Object} props - Component props
 * @param {string} props.elementID - Unique ID for the card element container
 * @param {boolean} props.isSaving - Whether the save operation is in progress
 * @param {Function} props.onSave - Callback executed when form is submitted with valid card
 * @param {Object} props.saveError - Error object from save operation
 * @param {Function} props.clearErrors - Function to clear errors
 * @param {string} props.submitButtonText - Text for submit button (default: "Save")
 * @param {string} props.className - Additional CSS classes for the form container
 */
export default function EBTCardForm({
    className = '',
    clearErrors,
    elementID = 'ebt-card-element-container',
    isSaving = false,
    onSave,
    saveError,
    submitButtonText = 'Save',
}) {
    const [cardElement, setCardElement] = useState(null);
    const [isCardValid, setIsCardValid] = useState(false);
    const [preSaveError, setPreSaveError] = useState(null);

    const handleReady = (element) => {
        setCardElement(element);
    };

    const handleChange = (_, isValid) => {
        clearErrors?.();
        setPreSaveError(null);
        setIsCardValid(isValid);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setPreSaveError(null);

        if (!isCardValid) {
            setPreSaveError('A valid EBT Card Number is required.');
            return;
        }

        if (!cardElement) {
            setPreSaveError('Card element not available.');
            return;
        }

        try {
            await onSave?.(cardElement);
        } catch (err) {
            logError(err, { component: 'EBTCardForm', action: 'submit' });
        }
    };

    return (
        <form onSubmit={handleSubmit} className={twclsx('space-y-15', className)}>
            <div>
                <ForageCardElement elementID={elementID} onReady={handleReady} onChange={handleChange} />
                {preSaveError && <p className="text-body-xs mt-10 text-error">{preSaveError}</p>}
            </div>
            <Button type="submit" disabled={!cardElement || isSaving}>
                {isSaving ? (
                    <span className="flex items-center gap-10">
                        <LoadingIcon />
                        Saving...
                    </span>
                ) : (
                    submitButtonText
                )}
            </Button>
            {saveError && (
                <EBTFormSubmissionError clearErrors={clearErrors} error={saveError} className="text-center" />
            )}
        </form>
    );
}
